﻿﻿﻿<script setup lang="ts">
/**
 * AppShell · 企业壳层
 * ------------------------------------------------------------
 * 原型对应：portal-header + #workContextBar + .app 三栏 grid + .tabbar。
 *
 * - 顶部：PortalHeader（68px 渐变头，右上角企业控件）
 * - 其下：WorkContextBar（46px 返回/首页/标题）
 * - .app：rail(252) | stage | side(310)，高度 = 100vh - 头 - 工作条 - 底栏
 * - 底部：BottomBar（58px 七项）
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import PortalHeader from './PortalHeader.vue'
import WorkContextBar from './WorkContextBar.vue'
import SidePanel from './SidePanel.vue'
import BottomBar from './BottomBar.vue'
import RailNav from './RailNav.vue'
import OnboardModal from '@/features/onboard/OnboardModal.vue'
import UToastHost from '@/design/ui/UToastHost.vue'
import LeakGuard from './LeakGuard.vue'
import AiBuddy from './AiBuddy.vue'
import { useScopeStore } from '@/domain/scope/store'
import { menuRepo } from '@/data/api/shell-repository'
import { menuNavPaths } from '@/domain/menu/nav'
import { initPortal } from './portal/portal'
import { isLoggedIn, authUser, logout } from '@/data/api/auth-store'
import { useSessionStore } from '@/domain/session/store'

const scope = useScopeStore()
const router = useRouter()
const sess = useSessionStore()
initPortal()

function onChatShortcut(e: KeyboardEvent) {
  if (!isLoggedIn.value || e.altKey || e.shiftKey || !(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'k') return
  e.preventDefault()
  sess.newSession()
  void router.push('/chat')
}
onMounted(() => {
  window.addEventListener('keydown', onChatShortcut)
  window.addEventListener('resize', applySideDefault)
  if (isLoggedIn.value && !scope.current) void scope.init()
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onChatShortcut)
  window.removeEventListener('resize', applySideDefault)
})

/* 左栏页面归属（铁律）：底栏按钮进入的主页面完全独立、互不影响，一律不挂菜单树；
   菜单树只属于「应用」体系：/apps 主页面 + 从菜单进入的菜单专有页面（/proc /components /help /building）；
   /chat 左栏是会话结构（chat 模式），与菜单树无关。
   railHidden 为折叠偏好：折叠把左栏收窄为 64px 图标窄轨（DOM 保留，复刻需求目录收窄效果），主区域随之变宽；railHidden 跨页记忆。 */
const route = useRoute()
const RAIL_HIDDEN_KEY = 'ema.rail.hidden.v1'
const railHidden = ref(localStorage.getItem(RAIL_HIDDEN_KEY) === '1')
function toggleRail() {
  railHidden.value = !railHidden.value
  try { localStorage.setItem(RAIL_HIDDEN_KEY, railHidden.value ? '1' : '0') } catch { /* 忽略 */ }
}
/* 收窄态图标轨点击分组/模块：展开回全宽（RailNav 内部已切换对应分组，无需再做） */
function expandRail() {
  railHidden.value = false
  try { localStorage.setItem(RAIL_HIDDEN_KEY, '0') } catch { /* 忽略 */ }
}
/* 独立主页面集合（底栏按钮/会话入口直达）：任何情况下不挂菜单树。菜单数据已收敛为独立系统
   （叶子不再指向这些路径），此处保留兜底：菜单数据若误配也不会把菜单带上独立主页面 */
const INDEPENDENT_PATHS = new Set(['/messenger', '/tasks', '/calendar', '/mail', '/cloud-space', '/arch', '/cloud-pc', '/profile'])
/* 菜单体系页面集合：/apps 与建设中页固定属于；菜单叶子目标页剔除独立主页面后保留菜单。
   壳层常驻且不随登录重建，因此菜单归属不能在启动时只拉一次（首次启动多停在登录页，那次请求必然 401），
   登录后与失败后的下一次导航都要重拉，否则从菜单点进页面时左栏会整体消失。 */
const BASE_MENU_PATHS = ['/apps', '/building']
/* 平台配置（/sys/*）属于菜单体系页：即使菜单接口暂不可用，点进配置页也必须保留左栏 */
const MENU_PREFIXES = ['/apps/', '/sys/']
const menuPaths = ref<Set<string>>(new Set(BASE_MENU_PATHS))
let menuPathsLoading = false
let menuPathsLoaded = false
async function loadMenuPaths() {
  if (menuPathsLoading) return
  menuPathsLoading = true
  try {
    const ms = await menuRepo.modules()
    const s = new Set<string>(BASE_MENU_PATHS)
    for (const m of ms) for (const l of m.items) {
      if (!l.route) continue
      const p = l.route.split('?')[0]
      if (!INDEPENDENT_PATHS.has(p)) s.add(p)
    }
    menuPaths.value = s
    menuPathsLoaded = true
  } catch {
    /* 登录前或接口暂不可用：保持现有集合，登录成功后或下次导航再重试 */
  } finally { menuPathsLoading = false }
}
void loadMenuPaths()
watch(isLoggedIn, v => { if (v && !menuPathsLoaded) void loadMenuPaths() })
watch(() => route.path, () => { if (isLoggedIn.value && !menuPathsLoaded) void loadMenuPaths() })

/* 左栏存在性：/chat=会话左栏；其余=菜单体系页。独立主页面无左栏（no-rail：grid 直接少一列）。
   菜单体系页同时认三条来源：/apps 与平台配置前缀、菜单叶子目标集合、以及「从菜单点进来过」的页面——
   最后一条保证点击菜单后左栏立即常驻，不依赖菜单接口是否已返回（接口失败也不丢左栏）。
   FDE（/fde/*）自带左侧导航、架构（/arch/*）为独立主页面，均不叠加系统左栏；
   产研工作台（/product/*）自 2026-11-04 起不再自渲染页内侧栏，由本左栏直接接管产研导航（RailNav sol 模式）。 */
const isOwnRailPage = computed(() =>
  !route.path.startsWith('/arch/') && !route.path.startsWith('/fde/'))
const railPage = computed(() => route.path === '/chat'
  || route.path.startsWith('/product/')
  || MENU_PREFIXES.some(p => route.path.startsWith(p))
  || (isOwnRailPage.value && (menuNavPaths.has(route.path) || menuPaths.value.has(route.path))))

/* 右栏折叠：与左栏同款交互（ema.side.hidden.v1）——折叠仅把右栏宽度收窄（DOM 保留，no-side → 0 宽），
   主区域随之变宽；折叠后经右缘手柄可再次展开。
   窄窗（≤1100px）默认收起：本地无记录时跟随即时窗口宽度（含 resize），
   用户一旦手动折叠/展开即写入本地，之后始终跟随用户选择不再自动变。 */
const SIDE_HIDDEN_KEY = 'ema.side.hidden.v1'
const SIDE_NARROW_W = 1100
const sideHidden = ref(localStorage.getItem(SIDE_HIDDEN_KEY) === '1'
  || (localStorage.getItem(SIDE_HIDDEN_KEY) === null && window.innerWidth <= SIDE_NARROW_W))
function applySideDefault() {
  if (localStorage.getItem(SIDE_HIDDEN_KEY) !== null) return /* 用户已手动选择，跟随用户 */
  sideHidden.value = window.innerWidth <= SIDE_NARROW_W
}
/* 右栏为平台级机制，全页面统一提供（含 /arch 架构工作台）；保留各页的折叠偏好。
   会话页（/chat）默认展开：右栏承载 AI 实时作业，进入会话即自动展开一次；
   用户手动折叠仍生效，只在再次进入会话路由时复位为展开（窄窗不自动展开，保持默认收起）。 */
const sidePage = computed(() => true)
function toggleSide() {
  sideHidden.value = !sideHidden.value
  try { localStorage.setItem(SIDE_HIDDEN_KEY, sideHidden.value ? '1' : '0') } catch { /* 忽略 */ }
}
watch(() => route.path, (path, prev) => {
  if (path === '/chat' && path !== prev && window.innerWidth > SIDE_NARROW_W) sideHidden.value = false
}, { immediate: true })

/* 左栏横向拖拽调宽：右缘把手（Pointer Capture 锁定事件），200~420px，双击复位；
   起点宽取把手按下时的实际渲染宽（未手动调过 = 响应式默认），增量叠加；宽度本机记忆 */
const RAIL_W_KEY = 'ema.rail.w.v1'
const railW = ref(Number(localStorage.getItem(RAIL_W_KEY)) || 0) /* 0 = 未手动调过，用 CSS 默认 */
const railResizing = ref(false)
/* 变量写 documentElement：grid 列 / railnav 宽 / 折叠手柄（在 .app 外，继承不到 .app 内联值）统一从这里解析 */
function applyRailVar(w: number) {
  document.documentElement.style.setProperty('--rail-w', w + 'px')
}
if (railW.value) applyRailVar(railW.value) /* 本机记忆宽度首帧即生效；未调过不写 0，回落断点默认 */
function railResizeDown(e: PointerEvent) {
  const el = e.currentTarget as HTMLElement
  const startW = railW.value || (el.parentElement as HTMLElement).getBoundingClientRect().width
  const startX = e.clientX
  try { el.setPointerCapture(e.pointerId) } catch { /* 捕获失败不影响 document 冒泡路径 */ }
  let done = false
  const move = (ev: PointerEvent) => {
    if (done) return
    railResizing.value = true
    railW.value = Math.round(Math.min(420, Math.max(200, startW + ev.clientX - startX)))
    applyRailVar(railW.value)
  }
  const up = () => {
    if (done) return
    done = true
    document.removeEventListener('pointermove', move)
    document.removeEventListener('pointerup', up)
    document.removeEventListener('pointercancel', up)
    railResizing.value = false
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
    try { localStorage.setItem(RAIL_W_KEY, String(railW.value)) } catch { /* 忽略 */ }
  }
  /* 统一挂 document（pointer 事件必冒泡；capture 仅保证拖出窗口仍收流）——实测此路径可靠 */
  document.addEventListener('pointermove', move)
  document.addEventListener('pointerup', up)
  document.addEventListener('pointercancel', up)
  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'
}
function railResizeReset() {
  railW.value = 0
  document.documentElement.style.removeProperty('--rail-w') /* 回落断点默认（252/236px） */
  try { localStorage.removeItem(RAIL_W_KEY) } catch { /* 忽略 */ }
}

/* 数据源标识（http/fake/mock；页面加载时读一次） */
const dsMode = localStorage.getItem('ema.backend') || 'mock'
const dsLabel = dsMode === 'http' ? 'HTTP' : dsMode === 'fake' ? 'FAKE' : 'MOCK'
/* 点击徽章循环切换数据源（mock→http→fake→mock），写入 localStorage 后刷新生效 */
function cycleDsMode() {
  const next = dsMode === 'mock' ? 'http' : dsMode === 'http' ? 'fake' : 'mock'
  /* 显式写入 'mock'：需求管理默认走真实库，只有明确选择 mock 才回落本地演示数据 */
  try {
    localStorage.setItem('ema.backend', next)
  } catch { /* 忽略 */ }
  location.reload()
}

/* 登录/登出 + bare 路由（登录页不显示壳层 chrome） */
const isBare = computed(() => route.meta.bare === true)
async function doLogout() {
  await logout()
  location.hash = '#/login'
}
</script>

<template>
  <PortalHeader v-if="!isBare" />
  <span v-if="!isBare" class="ds-badge" :data-mode="dsMode" :title="`数据源：${dsLabel}（点击切换 mock→http→fake）`" @click="cycleDsMode">{{ dsLabel }}</span>
  <span v-if="!isBare && isLoggedIn" class="auth-badge" :title="`已登录：${authUser?.name}`">
    {{ authUser?.name }} · <a @click="doLogout">登出</a>
  </span>
  <template v-if="!isBare">
    <WorkContextBar />
    <div class="app" :class="{ 'no-rail': !railPage, 'rail-off': railPage && railHidden, 'no-side': !sidePage || sideHidden, 'rail-resizing': railResizing }">
      <!-- 左栏 rail：有左栏的页面内 DOM 常驻；折叠收窄为 64px 图标窄轨（rail-off），主区域随之变宽 -->
      <aside v-if="railPage" class="rail" :class="{ off: railHidden }">
        <RailNav :mini="railHidden" @expand="expandRail" />
        <i v-if="!railHidden" class="rail-rs" title="拖拽调宽 · 双击复位" @pointerdown="railResizeDown" @dblclick="railResizeReset" />
      </aside>

      <!-- 中栏 stage -->
      <main class="stage">
        <div class="stage-inner">
          <RouterView />
        </div>
      </main>

      <!-- 右栏 side：全局常驻（当前上下文 / 本次操作与能力 / 助理记忆）；折叠仅收窄宽度（no-side → 0 宽），DOM 不销毁 -->
      <SidePanel v-if="sidePage" />
    </div>

    <BottomBar />
    <!-- 左栏折叠/展开手柄：与右栏手柄镜像对称，收纳于 rail 内部右下角；折叠后驻留屏幕左下角（仅 /chat /apps 显示） -->
    <button v-if="railPage" class="rail-toggle" :class="{ off: railHidden }"
            :title="railHidden ? '展开菜单' : '收起菜单'" :aria-label="railHidden ? '展开菜单' : '收起菜单'"
            @click="toggleRail">{{ railHidden ? '»' : '«' }}</button>
    <!-- 右栏折叠/展开手柄：镜像左栏手柄，收纳于 side 内部左下角；折叠后驻留屏幕右下角 -->
    <button v-if="sidePage" class="side-toggle" :class="{ off: sideHidden }"
            :title="sideHidden ? '展开信息栏' : '收起信息栏'" :aria-label="sideHidden ? '展开信息栏' : '收起信息栏'"
            @click="toggleSide">{{ sideHidden ? '«' : '»' }}</button>
    <!-- fresh 新手引导（自判断 isFresh + 未 onboarded 才弹一次） -->
    <OnboardModal />
    <!-- 全局轻提示（useToast 状态渲染点，点击条目关闭） -->
    <UToastHost />
    <!-- 悬浮 AI 助手（工作条 AI 按钮 toggle · 可拖拽 · 气泡说话） -->
    <AiBuddy />
    <!-- 防拍照水印（姓名/手机号平铺 + 隐形取证标记，全站每页叠加） -->
    <LeakGuard />
  </template>
  <RouterView v-else />
</template>

<style scoped>
.app {
  display: grid;
  grid-template-columns: var(--rail-w) minmax(0, 1fr) var(--side-w);
  height: calc(100vh - var(--portal-height) - var(--workbar-height) - 58px);
  background: var(--bg);
  transition: grid-template-columns .18s ease;
}

/* === rail === */
.rail {
  position: relative;
  background: linear-gradient(180deg, var(--surface), var(--surface-2));
  border-right: 1px solid var(--line);
  padding: var(--sp-4) var(--sp-3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}
/* 内容固定宽：折叠收窄时只裁剪不压缩；宽度取 rail 内容盒（列宽减左右 padding），否则内部 margin:auto 居中基准整体右偏、右侧被 overflow 裁掉 */
.rail :deep(.railnav) { width: calc(var(--rail-w) - 2 * var(--sp-3)); box-sizing: border-box; flex: none; }
/* 右缘拖拽把手：悬停/拖拽中显示高亮条；拖拽中关闭 grid 过渡避免跟手滞后 */
.rail-rs { position: absolute; top: 0; right: 0; width: 7px; height: 100%; cursor: col-resize; z-index: 30; touch-action: none; }
.rail-rs:hover { background: var(--accent-line); opacity: .45; }
.app.rail-resizing { transition: none; }
.app.rail-resizing .rail-rs { background: var(--accent-line); opacity: .6; }

/* === stage === */
.stage { display: flex; flex-direction: column; overflow: hidden; min-width: 0; }
.stage-inner { flex: 1; overflow: auto; }

/* === 折叠态（DOM 均保留，左栏收窄为 64px 图标窄轨，stage 随之占满剩余空间） === */
/* 左栏折叠：rail-off → 64px 收窄（复刻需求目录收窄：宽度过渡 .18s 已由 .app grid 过渡承担） */
.app.rail-off { grid-template-columns: 64px minmax(0, 1fr) var(--side-w); }
.app.rail-off .rail { padding: var(--sp-4) 0; }
/* 收窄态 railnav 不再按全宽裁剪，占满 64px 窄轨居中排图标 */
.app.rail-off .rail :deep(.railnav) { width: 100%; }
/* 右栏折叠：no-side → 第三列 0 宽（SidePanel DOM 保留仅隐藏） */
.app.no-side { grid-template-columns: var(--rail-w) minmax(0, 1fr) 0px; }
.app.no-side .side { border-left-color: transparent; padding: 0; }
/* 双折叠组合 */
.app.rail-off.no-side { grid-template-columns: 64px minmax(0, 1fr) 0px; }

/* === 独立主页面（底栏按钮直达）：无左栏（no-rail 不挂载 rail），stage 占第一列（不残留空列挤占 stage） === */
.app.no-rail { grid-template-columns: minmax(0, 1fr) var(--side-w); }
.app.no-rail.no-side { grid-template-columns: minmax(0, 1fr) 0px; }

/* === 折叠/展开手柄：左右镜像一对，收纳于各自边栏内部底角（不凸出、不遮挡中间 stage）；折叠后分别驻留屏幕左/右下角 === */
.rail-toggle, .side-toggle {
  position: fixed;
  bottom: 66px; /* 底栏 58px + 8px 间距 */
  width: 17px; height: 46px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink-3);
  font-size: 11px; line-height: 1;
  display: grid; place-items: center;
  cursor: pointer;
  z-index: 40;
  box-shadow: 0 1px 6px #00000014;
}
.rail-toggle {
  left: calc(var(--rail-w) - 17px); /* 贴 rail 内部右下角（分界线内侧） */
  border-radius: 8px;
}
.side-toggle {
  right: calc(var(--side-w) - 17px); /* 贴 side 内部左下角（分界线内侧） */
  border-radius: 8px;
}
.rail-toggle:hover, .side-toggle:hover { color: var(--accent); background: var(--hover); }
.rail-toggle.off { left: 47px; border-radius: 8px; box-shadow: 0 1px 6px #00000014; }
.side-toggle.off { right: 0; border-radius: 8px 0 0 8px; box-shadow: -2px 0 6px #00000014; }

/* === 响应式（对齐原型断点） === */
/* 中屏（≤1320px）/窄屏（≤1100px）：栏宽只由 :root 的 --rail-w / --side-w 断点变量承担（见下方全局块），
   grid 列统一引用变量，不再逐断点覆盖——否则 .app.rail-off/.app.no-rail 等更高优先级规则会顶住断点值，
   列宽与变量漂移后折叠手柄错位 */
/* 极窄屏（≤780px）：side 与 rail 均由断点隐藏，stage 独占（页面导航走底栏）。
   必须给满单列：只写两条轨道时 rail 会占满 1fr、stage 被压进 0 宽列，主页面整体消失 */
@media (max-width: 780px) {
  .app, .app.no-side, .app.rail-off, .app.rail-off.no-side, .app.no-rail, .app.no-rail.no-side { grid-template-columns: minmax(0, 1fr); }
  .rail, .side { display: none; }
  .rail-toggle, .side-toggle { display: none; }
}

</style>

<style>
/* SidePanel 根元素在 780 断点收起（scoped 选择器够不到，全局补一条） */
@media (max-width: 780px) { .side { display: none !important; } }
/* 栏宽变量（折叠手柄定位用；side 默认 310px，断点内会被覆盖）。
   注意：:root 覆盖必须放在本非 scoped 块中——scoped 块里的 :root 会带上组件 scope 属性，永远匹配不到 <html> */
:root { --rail-w: 252px; --side-w: 310px; }
@media (max-width: 1320px) { :root { --rail-w: 236px; --side-w: 280px; } }
@media (max-width: 1100px) { :root { --rail-w: 200px; --side-w: 260px; } }
/* 数据源标识 badge */
.ds-badge { position: fixed; top: 8px; right: 12px; z-index: 100; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; letter-spacing: .5px; background: var(--surface); border: 1px solid var(--line); cursor: pointer; user-select: none; }
.ds-badge:hover { filter: brightness(.96); }
.ds-badge[data-mode="http"] { color: #16a34a; border-color: #16a34a; }
.ds-badge[data-mode="fake"] { color: #ca8a04; border-color: #ca8a04; }
.ds-badge[data-mode="mock"] { color: #6b7280; }
.auth-badge { position: fixed; top: 8px; right: 62px; z-index: 100; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; background: var(--surface); border: 1px solid var(--line); }
.auth-badge a { cursor: pointer; color: #3b82f6; }
.auth-badge a:hover { text-decoration: underline; }
</style>
